import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Button from "../../../components/Button";
import Loading from "../../../components/Loaders/Loading";
import { useAuthentication } from "../../Authentication/context";
import { useSubDomain } from "../../../utils/hooks/useSubDomain";
import PageShell from "./PageShell";
import { useHibobApi } from "./api";
import { HIBOB_SCOPES, getScopeText } from "./scopes";
import { isValidStartUrl } from "./startUrl";
import {
	ADMIN_ONLY,
	GENERIC_ERROR,
	TENANT_DOMAIN_MISSING,
	TOO_MANY_ATTEMPTS,
} from "./messages";

const SCOPES = Object.keys(HIBOB_SCOPES);

function Install() {
	const { t } = useTranslation();
	const { accessMap, companyName, company } = useAuthentication();
	const canManage = !!accessMap?.integrations?.canManage;
	const tenantName = companyName || "your Traxxion account";
	const subDomain = useSubDomain();

	const { getStatus, createSetupSession } = useHibobApi();

	// loading | confirm | connected | forbidden | error
	const [view, setView] = useState("loading");
	const [installation, setInstallation] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(null);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const loadStatus = useCallback(async () => {
		setView("loading");
		try {
			const response = await getStatus();
			// No response: the session expired and the app is reloading to the login.
			if (!mounted.current || !response) {
				return;
			}
			if (response.status === 403) {
				setView("forbidden");
			} else if (response.status !== 200 || !response.data?.status) {
				setView("error");
			} else {
				setInstallation(response.data);
				setView(response.data.status === "connected" ? "connected" : "confirm");
			}
		} catch (err) {
			if (mounted.current) {
				setView("error");
			}
		}
	}, [getStatus]);

	useEffect(() => {
		if (canManage) {
			loadStatus();
		}
	}, [canManage, loadStatus]);

	const onContinue = useCallback(async () => {
		setSubmitting(true);
		setSubmitError(null);
		try {
			const response = await createSetupSession();
			if (!mounted.current || !response) {
				return;
			}
			const { status, data } = response;
			if (status === 201 && isValidStartUrl(data?.startUrl, subDomain)) {
				// Top-level navigation to this tenant's API host: it sets its cookie
				// there and forwards to HiBob.
				// The button stays disabled while the browser leaves the page.
				window.location.assign(data.startUrl);
				return;
			}
			setSubmitting(false);
			if (status === 409 && data?.reason === "already_connected") {
				loadStatus();
			} else if (status === 409 && data?.reason === "tenant_domain_missing") {
				setSubmitError(TENANT_DOMAIN_MISSING);
			} else if (status === 403) {
				setView("forbidden");
			} else if (status === 429) {
				setSubmitError(TOO_MANY_ATTEMPTS);
			} else {
				setSubmitError(GENERIC_ERROR);
			}
		} catch (err) {
			if (mounted.current) {
				setSubmitting(false);
				setSubmitError(GENERIC_ERROR);
			}
		}
	}, [createSetupSession, loadStatus, subDomain]);

	if (!canManage || view === "forbidden") {
		return (
			<PageShell title="Connect HiBob">
				<p role="alert">{ADMIN_ONLY}</p>
				<Button color="primary" tag={Link} to="/">
					Back to Traxxion
				</Button>
			</PageShell>
		);
	}

	if (view === "loading") {
		return (
			<PageShell title="Connect HiBob">
				<div className="text-center py-4">
					<Loading />
				</div>
			</PageShell>
		);
	}

	if (view === "error") {
		return (
			<PageShell title="Connect HiBob">
				<p role="alert">{GENERIC_ERROR}</p>
				<Button color="primary" onClick={loadStatus}>
					Try again
				</Button>
				<Link to="/" className="ml-3">
					Cancel
				</Link>
			</PageShell>
		);
	}

	if (view === "connected") {
		const connectedAt = installation?.connectedAt
			? moment(installation.connectedAt)
			: null;
		const connectedBy = installation?.connectedBy?.name;
		return (
			<PageShell title="HiBob is already connected">
				<p>
					HiBob is already connected to <strong>{tenantName}</strong>.
				</p>
				{(connectedAt?.isValid() || connectedBy) && (
					<p className="text-muted">
						Connected
						{connectedAt?.isValid() &&
							` on ${connectedAt.format(company?.settings?.dateFormat || "MMM D, YYYY")}`}
						{connectedBy && ` by ${connectedBy}`}.
					</p>
				)}
				<Button color="primary" tag={Link} to="/">
					Go to Traxxion
				</Button>
			</PageShell>
		);
	}

	return (
		<PageShell title={`Connect HiBob to ${tenantName}`}>
			<p>
				You'll be sent to HiBob to approve access. Traxxion will read employee,
				organization and time-off data, and can create time-off requests in HiBob.
			</p>
			<ul className="pl-4">
				{SCOPES.map((scope) => {
					const { label, purpose } = getScopeText(scope, t);
					return (
						<li key={scope}>
							{label}: <span className="text-muted">{purpose}</span>
						</li>
					);
				})}
			</ul>
			<p className="text-muted text-sm">
				Make sure you're signed in to HiBob as an admin of the company you want to
				connect.
			</p>
			{submitError && (
				<p role="alert" className="text-danger">
					{submitError}
				</p>
			)}
			<Button
				color="primary"
				onClick={onContinue}
				loading={submitting}
				disabled={submitting}
			>
				Continue to HiBob
			</Button>
			<Link to="/" className="ml-3">
				Cancel
			</Link>
		</PageShell>
	);
}

export default Install;
